import PropTypes from 'prop-types';
import deleteImage from 'assets/icon-delete.svg';
import Button from 'Components/Atoms/Button/Button';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import FormProvider, { RHFInput } from 'Components/Organisms/HookForm';

const magicWords = {
    ES: 'ELIMINAR',
    EN: 'DELETE',
};

export default function DeleteConfirmDialogContent(props) {
    const { onClose, onSubmit, title, onError, description, cta, icon, showCaptcha } = props;

    const { t, i18n } = useTranslation('common');
    const magicWord = magicWords[i18n.language] || magicWords.EN;

    const hasIcon = Boolean(icon);
    const ctaText = cta || t('delete');

    const captchaSchema = yup.object().shape({
        captcha: yup
            .string()
            .required()
            .transform((_value, originalValue) => originalValue.toUpperCase().trim())
            .oneOf([magicWord], t('invalid-magic-word', { magicWord })),
    });

    const emptySchema = yup.object().shape({});

    const schema = showCaptcha ? captchaSchema : emptySchema;

    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            captcha: '',
        },
    });

    const {
        handleSubmit,
        formState: { isSubmitting },
    } = methods;

    const onFormSubmit = async () => {
        try {
            await onSubmit?.();
        } catch (error) {
            if (typeof onError === 'function') {
                onError(error);
            } else {
                throw error;
            }
        }
    };

    return (
        <div className="py-8 md:py-10 px-2 md:px-6">
            <FormProvider methods={methods} onSubmit={handleSubmit(onFormSubmit)}>
                <div className="text-center flex flex-col items-center gap-3 mb-8">
                    <div className="flex justify-center mb-2">
                        {hasIcon ? icon : <img src={deleteImage} alt="" width={48} height={48} />}
                    </div>
                    <div className="text-xl font-semibold text-dark-blue">{title}</div>
                    <div className="text-gray-500 text-pretty text-lg">{description}</div>
                    {showCaptcha && (
                        <div className="py-6 w-full text-left">
                            <RHFInput
                                autoFocus
                                label={t('write-magic-word-to-confirm', { magicWord })}
                                name="captcha"
                            />
                        </div>
                    )}
                </div>
                <div className="flex justify-evenly gap-3">
                    <Button variant="primary" type="submit" className="grow" isBusy={isSubmitting}>
                        {ctaText}
                    </Button>
                    <Button onClick={onClose} className="grow" disabled={isSubmitting}>
                        {t('cancel')}
                    </Button>
                </div>
            </FormProvider>
        </div>
    );
}

DeleteConfirmDialogContent.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    cta: PropTypes.string,
    icon: PropTypes.node,
    showCaptcha: PropTypes.bool,
    onError: PropTypes.func,
};

DeleteConfirmDialogContent.dialogProps = {
    size: 'sm',
};
